import { mongoose } from "@typegoose/typegoose";
import { cacheContext } from "../../caching/index.js";
import { dayMs, hourMs, secondMs } from "../../timeConstants.js";
import { parse, stringify } from "../../utils/jsonSerializer/index.js";
import { AssetIdentity } from "./assetIdentity.js";

/** The whole identity — same shape as the document, so callers can treat the two alike. */
export type AssetIdentityRecord = Pick<
    AssetIdentity,
    "_id" | "schemaKey" | "verdiId" | "loraEui" | "assetType" | "assetCollection"
>;

/** Stored form: schemaKey dropped (a constant, restamped on read), _id flattened to hex because the
 *  default codec turns an ObjectId into a bare string with no way back. */
type StoredAssetIdentity = Omit<AssetIdentityRecord, "_id" | "schemaKey"> & { _id: string };

export const encodeAssetIdentity = (record: AssetIdentityRecord | null): string => {
    if (!record) return stringify(null, { htmlScriptSafe: false });
    const { schemaKey: _schemaKey, _id, ...rest } = record;
    return stringify({ ...rest, _id: _id.toHexString() } satisfies StoredAssetIdentity, {
        htmlScriptSafe: false,
    });
};

export const decodeAssetIdentity = (value: string): AssetIdentityRecord | null => {
    const stored = parse(value) as StoredAssetIdentity | null;
    if (!stored) return null;
    return {
        ...stored,
        _id: new mongoose.Types.ObjectId(stored._id),
        schemaKey: AssetIdentity.schemaKey,
    };
};

/** Looks up an asset identity by LoRa EUI.
 *
 *  Decode trusts what it reads, and a payload predating _id would decode into a random one
 *  (`new ObjectId(undefined)` invents one rather than failing). The version suffix retires such keys
 *  without a bust, and is the only thing keeping an old shape out: bump it on any change to the
 *  cached fields. */
export const assetIdentityByLoraEui = cacheContext.registerCachedAction<[string], AssetIdentityRecord | null>(
    "asset-identity-by-lora-eui-v2",
    {
        // Both windows are additional time on top of ttlMs, so in absolute terms: fresh for 6h, stale
        // served with a background refresh out to 18h, last good value served out to 30h but only while
        // the source is failing.
        ttlMs: 6 * hourMs,
        staleTtlMs: 12 * hourMs,
        persistWhenUnavailableMs: dayMs,
        // A by-EUI point read is single-digit ms when Mongo is healthy. Fail fast so a hung fetch cannot
        // pin every Device pre("validate") for the 5-minute default instead of serving the persisted value.
        fetchTimeoutMs: 5 * secondMs,
        // Read on every Device pre("validate"), ~600/min in production; 10% puts diagnostics
        // near 60 lines/min.
        diagnosticsSampleRate: 0.1,
        encodeValue: encodeAssetIdentity,
        decodeValue: decodeAssetIdentity,
    },
);
